import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ChartComponent } from 'ng-apexcharts';
import { catchError, map, of } from 'rxjs';
import { OkrMetricOption } from 'src/app/models/okr-management.model';
import { ApiService } from 'src/app/services/api.service';

export interface CompanyObjectiveKeyResultMetricSelectorDialogData {
  company_product_id: number;
  selected_metric_id: number | null;
}

@Component({
  selector: 'app-company-objective-key-result-metric-selector-dialog',
  templateUrl: './company-objective-key-result-metric-selector-dialog.component.html',
  styleUrl: './company-objective-key-result-metric-selector-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdkScrollable,
    MatButtonModule,
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    MatFormFieldModule,
    MatInputModule,
    ChartComponent,
  ],
})
export class CompanyObjectiveKeyResultMetricSelectorDialogComponent {
  private readonly dialog_ref = inject(MatDialogRef<CompanyObjectiveKeyResultMetricSelectorDialogComponent, OkrMetricOption>);
  private readonly api = inject(ApiService);
  readonly data = inject<CompanyObjectiveKeyResultMetricSelectorDialogData>(MAT_DIALOG_DATA);

  readonly search = signal('');
  readonly metrics = toSignal(
    this.api.getOkrMetrics(this.data.company_product_id).pipe(
      map(response => (response.company_metrics ?? []).map(metric => this.serializeMetric(metric))),
      catchError(() => of([] as MetricCard[]))
    ),
    { initialValue: [] as MetricCard[] }
  );
  readonly filtered_metrics = computed(() => {
    const term = this.search().trim().toLowerCase();
    const metrics = this.metrics();
    if (term === '') {
      return metrics;
    }

    return metrics.filter(metric => metric.title.toLowerCase().includes(term));
  });

  onSearch(event: Event): void {
    const target = event.target;
    if (target instanceof HTMLInputElement) {
      this.search.set(target.value);
    }
  }

  selectMetric(metric: MetricCard): void {
    this.dialog_ref.close(metric.source);
  }

  close(): void {
    this.dialog_ref.close();
  }

  private serializeMetric(metric: OkrMetricOption): MetricCard {
    const raw_chart = metric.visualizations?.[0]?.json ?? null;
    const chart = raw_chart === null ? null : compactChartOptions(raw_chart);
    const simple_value = metric.value_text ?? (metric.value != null ? String(metric.value) : null);

    return {
      id: metric.id,
      title: metric.title,
      is_selected: this.data.selected_metric_id === metric.id,
      chart,
      show_chart: chart !== null,
      show_simple: chart === null && simple_value !== null && simple_value !== '',
      simple_value,
      simple_units: metric.value_unit ?? null,
      show_empty: chart === null && !(simple_value !== null && simple_value !== ''),
      source: metric,
    };
  }
}

interface MetricCard {
  id: number;
  title: string;
  is_selected: boolean;
  chart: Record<string, unknown> | null;
  show_chart: boolean;
  show_simple: boolean;
  simple_value: string | null;
  simple_units: string | null;
  show_empty: boolean;
  source: OkrMetricOption;
}

function compactChartOptions(chart: Record<string, unknown>): Record<string, unknown> {
  const chart_config =
    chart['chart'] !== null && typeof chart['chart'] === 'object' ? { ...(chart['chart'] as Record<string, unknown>) } : {};

  return {
    ...chart,
    chart: {
      ...chart_config,
      height: 120,
      toolbar: { show: false },
      animations: { enabled: false },
    },
    legend: { show: false },
    dataLabels: { enabled: false },
  };
}
